import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "@/lib/api/endpoints";
import { Button, Field, Input, useToast } from "@/components/ui";
import { BrandMark } from "@/features/admin/components/BrandMark";

/** Target of the link in both password-reset and invitation emails. */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const toast = useToast();

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirmation.length > 0 && password !== confirmation;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (mismatch) return;

    setSubmitting(true);
    setError(null);
    try {
      await authApi.resetPassword(token, password, confirmation);
      toast.success("Password set. Please sign in.");
      navigate("/admin/login", { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not set your password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-full place-items-center px-4 py-12">
      <div className="w-full max-w-[420px] animate-slide-up">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <BrandMark size={52} />
          <h1 className="text-2xl font-bold tracking-tight">Set your password</h1>
        </div>

        {token ? (
          <form onSubmit={onSubmit} className="panel flex flex-col gap-4 p-6">
            <Field label="New password" htmlFor="password" hint="At least 8 characters.">
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            <Field label="Confirm password" htmlFor="confirmation" error={mismatch ? "Passwords don't match" : undefined}>
              <Input
                id="confirmation"
                type="password"
                autoComplete="new-password"
                required
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </Field>
            {error && (
              <p className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
            )}
            <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting} disabled={mismatch}>
              Set password
            </Button>
          </form>
        ) : (
          <div className="panel p-6 text-center text-sm text-haze-300">
            <p>This link is missing its token. Request a new one from the sign-in page.</p>
            <Link to="/admin/login" className="mt-3 inline-block text-accent hover:underline">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
